import { RawDraftContentState } from 'draft-js';
import { isNull } from 'lodash/fp';

import { AgreementType } from '../../../admin/documents/store/types';
import { ArkDocument, AnalysisView } from '../../../documents/store/types';
import { DropdownOption } from '../../../shared/dropdown/Dropdown';
import { ColumnSort, TableFilters } from '../../../shared/table/types';
import { CloseOutNettingSummary, CollateralisationMethodSummary, NettingEngineSummary } from '../../store/types';

export const isIsdaNettingData = (nettingDocument: ISDANettingEngineData | GMRANettingEngineData | null): nettingDocument is ISDANettingEngineData => !isNull(nettingDocument) && (nettingDocument.agreement === NettingEngineAgreement.ISDA_1992_MASTER || nettingDocument.agreement === NettingEngineAgreement.ISDA_2002_MASTER);

export enum OpinionNettingEngineActionTypes {
    SET_NETTING_ENGINE_PAGE = 'SET_NETTING_ENGINE_PAGE',
    SET_NETTING_ENGINE_AGREEMENT_TYPE = 'SET_NETTING_ENGINE_AGREEMENT_TYPE',
    RESET_NETTING_ENGINE_PAGE = 'RESET_NETTING_ENGINE_PAGE',
    UPDATE_NETTING_ENGINE_DATA = 'UPDATE_NETTING_ENGINE_DATA',
    CALCULATE_NETTING_ENGINE_STARTED = 'CALCULATE_NETTING_ENGINE_STARTED',
    CALCULATE_NETTING_ENGINE_SUCCESSFUL = 'CALCULATE_NETTING_ENGINE_SUCCESSFUL',
    CALCULATE_NETTING_ENGINE_FAILED = 'CALCULATE_NETTING_ENGINE_FAILED',
    SET_SURVEY_QUESTION_INDEX = 'SET_SURVEY_QUESTION_INDEX',
    RESET_ENGINE_DATA_STATE = 'RESET_ENGINE_DATA_STATE',
    SET_SHOW_SUMMARY = 'SET_SHOW_SUMMARY',
    SET_LEI_MODAL_OPEN = 'SET_LEI_MODAL_OPEN',
    SET_CHANGING_QUESTION = 'SET_CHANGING_QUESTION',
    SET_NETTING_ASSUMPTIONS_MODAL_OPEN = 'SET_NETTING_ASSUMPTIONS_MODAL_OPEN',
    ENQUIRE_ALL_NETTING_OPINIONS_STARTED = 'ENQUIRE_ALL_NETTING_OPINIONS_STARTED',
    ENQUIRE_SINGLE_NETTING_OPINION_STARTED = 'ENQUIRE_SINGLE_NETTING_OPINION_STARTED',
    ENQUIRE_NETTING_OPINION_SUCCESSFUL = 'ENQUIRE_NETTING_OPINION_SUCCESSFUL',
    ENQUIRE_NETTING_OPINION_FAILED = 'ENQUIRE_NETTING_OPINION_FAILED',
    TOGGLE_INDUSTRY_OR_CUSTOM_CONCLUSIONS = 'TOGGLE_INDUSTRY_OR_CUSTOM_CONCLUSIONS',
    FETCH_NETTING_ENGINE_DOCUMENTS_STARTED = 'FETCH_NETTING_ENGINE_DOCUMENTS_STARTED',
    FETCH_NETTING_ENGINE_DOCUMENTS_SUCCESSFUL = 'FETCH_NETTING_ENGINE_DOCUMENTS_SUCCESSFUL',
    FETCH_NETTING_ENGINE_DOCUMENTS_FAILED = 'FETCH_NETTING_ENGINE_DOCUMENTS_FAILED',
    SET_NETTING_ENGINE_DOCUMENT_TABLE_FILTERS = 'SET_NETTING_ENGINE_DOCUMENT_TABLE_FILTERS',
    CLEAR_NETTING_ENGINE_DOCUMENT_TABLE_FILTERS = 'CLEAR_NETTING_ENGINE_DOCUMENT_TABLE_FILTERS',
    SET_NETTING_ENGINE_DOCUMENT_TABLE_COLUMN_SORT = 'SET_NETTING_ENGINE_DOCUMENT_TABLE_COLUMN_SORT',
    SET_NETTING_ENGINE_DOCUMENTS_PAGE_SIZE = 'SET_NETTING_ENGINE_DOCUMENTS_PAGE_SIZE',
    NETTING_ENGINE_DOCUMENTS_PAGINATION_NEXT = 'NETTING_ENGINE_DOCUMENTS_PAGINATION_NEXT',
    NETTING_ENGINE_DOCUMENTS_PAGINATION_PREVIOUS = 'NETTING_ENGINE_DOCUMENTS_PAGINATION_PREVIOUS',
    OPEN_NETTING_ENGINE_DOCUMENT_STARTED = 'OPEN_NETTING_ENGINE_DOCUMENT_STARTED',
    OPEN_NETTING_ENGINE_DOCUMENT_SUCCESSFUL = 'OPEN_NETTING_ENGINE_DOCUMENT_SUCCESSFUL',
    OPEN_NETTING_ENGINE_DOCUMENT_FAILED = 'OPEN_NETTING_ENGINE_DOCUMENT_FAILED',
    UPDATE_NETTING_ENGINE_FIELDS = 'UPDATE_NETTING_ENGINE_FIELDS',
    TOGGLE_NETTING_ENGINE_VIEW = 'TOGGLE_NETTING_ENGINE_VIEW',
    TOGGLE_DOCUMENT_SUMMARY_VIEW = 'TOGGLE_DOCUMENT_SUMMARY_VIEW',
    SET_SELECTED_PRODUCT_COVERED = 'SET_SELECTED_PRODUCT_COVERED',
    SET_SELECTED_JURISDICTION_DETAILS = 'SET_SELECTED_JURISDICTION_DETAILS',
    SET_ANSWER_JUSTIFICATION_MODAL_OPEN = 'SET_ANSWER_JUSTIFICATION_MODAL_OPEN',
    DOWNLOAD_NETTING_ENGINE_RESULTS_STARTED = 'DOWNLOAD_NETTING_ENGINE_RESULTS_STARTED',
    DOWNLOAD_NETTING_ENGINE_RESULTS_SUCCESSFUL = 'DOWNLOAD_NETTING_ENGINE_RESULTS_SUCCESSFUL',
    DOWNLOAD_NETTING_ENGINE_RESULTS_FAILED = 'DOWNLOAD_NETTING_ENGINE_RESULTS_FAILED',
    EMAIL_NETTING_ENGINE_RESULTS_STARTED = 'EMAIL_NETTING_ENGINE_RESULTS_STARTED',
    EMAIL_NETTING_ENGINE_RESULTS_SUCCESSFUL = 'EMAIL_NETTING_ENGINE_RESULTS_SUCCESSFUL',
    EMAIL_NETTING_ENGINE_RESULTS_FAILED = 'EMAIL_NETTING_ENGINE_RESULTS_FAILED',
    TOGGLE_NETTING_ENGINE_EMAIL_MODAL_OPEN = 'TOGGLE_NETTING_ENGINE_EMAIL_MODAL_OPEN',
    UPDATE_NETTING_ENGINE_EMAIL_VALUE = 'UPDATE_NETTING_ENGINE_EMAIL_VALUE',
    DOCUMENT_NETTING_DETAILS_AUTO_UPDATE_COMPLETED = 'DOCUMENT_NETTING_DETAILS_AUTO_UPDATE_COMPLETED'
}

export enum NettingPage {
    SELECT = 'Select',
    DOCUMENTS = 'Existing Documents',
    THEORETICAL = 'Theoretical Agreements'
}

export enum NettingEngineAgreement {
    ISDA_1992_MASTER = '1992 ISDA Master Agreement (Multicurrency - Cross Border)',
    ISDA_2002_MASTER = '2002 ISDA Master Agreement',
    GMRA_2000 = '2000 TBMA/ISMA Global Master Repurchase Agreement',
    GMRA_2011 = '2011 ICMA/SIFMA Global Master Repurchase Agreement'
}

export enum NettingEngineQuestionType {
    BOOLEAN = 'boolean',
    DROPDOWN = 'dropdown'
}

export enum NettingEngineTableView {
    EXISTING = 'EXISTING',
    PRE_EXECUTION = 'PRE_EXECUTION'
}

export interface TheoryParty {
    counterpartyType: string | null;
    jurisdiction: string | null;
}

export interface NettingEngineCounterparty {
    entityId: string | null;
    jurisdiction: string | null;
    counterpartyClassification: string | null;
    subCounterpartyTypeId: number | null;
}

interface NettingEngineDataBase {
    counterpartyNameKnown: boolean | null;
    counterparty: NettingEngineCounterparty;
    agreement: NettingEngineAgreement | null;
    productTypesKnown: boolean | null;
    productsTraded: string[];
    counterpartyOffices: string[];
}

export interface ISDANettingEngineData extends NettingEngineDataBase {
    governingLaw: string | null;
    counterpartyIsMultiBranch: boolean | null;
    sectionTenAApplies?: string | null;
    paymentMethod?: string | null;
    automaticEarlyTerminationApplicable?: string | null;
}

export interface GMRANettingEngineData extends NettingEngineDataBase {
    counterpartyIsMultiDesignatedCities: boolean | null;
}

export type NettingEngineData = ISDANettingEngineData | GMRANettingEngineData;

export interface NettingEngineSubQuestion {
    question: string;
    value: DropdownOption | null;
    dropdownOptions: DropdownOption[];
    key: keyof ISDANettingEngineData | keyof GMRANettingEngineData;
    counterpartyKey?: keyof NettingEngineCounterparty;
}

export interface NettingEngineSurvey {
    question: string;
    type: NettingEngineQuestionType;
    value: DropdownOption | DropdownOption[] | boolean | null;
    key: keyof ISDANettingEngineData | keyof GMRANettingEngineData;
    dropdownOptions?: DropdownOption[];
    counterpartyKey?: keyof NettingEngineCounterparty;
    subQuestion?: NettingEngineSubQuestion;
    showSubQuestion?: boolean;
    isMulti?: boolean;
    closeOnSelect?: boolean;
    note?: string;
}

export interface NettingEngineDocumentSummaryQuestion extends NettingEngineSurvey {
    isHidden?: boolean;
}

export enum ProductTypeCoveredEnum {
    YES = 'Yes',
    NO = 'No',
    UNCLEAR = 'Unclear'
}

export enum NettingEngineValidGoverningLaw {
    ENGLAND = 'England',
    FRANCE = 'France',
    IRELAND = 'Ireland',
    NEW_YORK = 'New York'
}

export interface ISDANettingRequestData {
    counterpartyId: string;
    counterpartyJurisdiction: string;
    governingLawJurisdiction: string;
    agreementType: NettingEngineAgreement;
    counterpartyType: string;
    subCounterpartyTypeId?: number;
    productTypes: string[];
    branches: string[];
    sectionTenAApplies?: string;
    paymentMethod?: string;
    automaticEarlyTerminationApplicable?: string;
}

export interface GMRANettingRequestData {
    counterpartyId: string;
    counterpartyJurisdiction: string;
    agreementType: NettingEngineAgreement;
    counterpartyType: string;
    subCounterpartyTypeId?: number;
    productTypes: string[];
    branches: string[];
}

export type NettingRequestData = ISDANettingRequestData | GMRANettingRequestData;

export interface ProductTypeCovered {
    product: string;
    covered: ProductTypeCoveredEnum;
    conditions?: string;
    notes?: RawDraftContentState;
}

export interface JurisdictionalNettingDetailsAnswer {
    value: string;
    details: RawDraftContentState | null;
}

export interface BaseJurisdictionalNettingDetails {
    agreementCovered: JurisdictionalNettingDetailsAnswer;
    automaticEarlyTerminationRequired: JurisdictionalNettingDetailsAnswer;
    closeOutNettingEnforceable: JurisdictionalNettingDetailsAnswer;
    counterpartyCovered: JurisdictionalNettingDetailsAnswer;
    governingLawRecognised: JurisdictionalNettingDetailsAnswer;
    offShoreInsolvency: JurisdictionalNettingDetailsAnswer;
    onShoreInsolvency: JurisdictionalNettingDetailsAnswer;
    productsCovered: ProductTypeCovered[];
}

export interface ISDAJurisdictionalNettingDetails extends BaseJurisdictionalNettingDetails {
    automaticEarlyTerminationApplicable?: JurisdictionalNettingDetailsAnswer;
    localLawIsValid: JurisdictionalNettingDetailsAnswer | null;
}

export interface GMRAJurisdictionalNettingDetails extends BaseJurisdictionalNettingDetails {
    multipleOfficesImpactAnswer: JurisdictionalNettingDetailsAnswer | null;
}

export type JurisdictionalNettingDetails = ISDAJurisdictionalNettingDetails | GMRAJurisdictionalNettingDetails;

export interface ISDAJurisdictionalNettingInformation {
    details: ISDAJurisdictionalNettingDetails;
    jurisdiction: string;
    counterpartyOffices: string[];
    summary: CloseOutNettingSummary;
    permittedToView: boolean;
    clientSignOffDetails: JurisdictionalNettingDetails | null;
    clientSignOffSummary: CloseOutNettingSummary;
    svgBase64?: string;
}

export interface GMRAJurisdictionalCollateralNettingDetails {
    recharacterisationAsSecurityAdverselyImpactsEnforceability: JurisdictionalNettingDetailsAnswer;
    rightToSetOffEnforceable: JurisdictionalNettingDetailsAnswer;
    transferRespectedAsTitleTransfer: JurisdictionalNettingDetailsAnswer;
}

export interface GMRAJurisdictionalNettingInformation extends Omit<ISDAJurisdictionalNettingInformation, 'details' | 'clientSignOffDetails'> {
    details: GMRAJurisdictionalNettingDetails;
    clientSignOffDetails: GMRAJurisdictionalNettingDetails | null;
    collateralDetails: GMRAJurisdictionalCollateralNettingDetails;
    collateralSummary: CollateralisationMethodSummary;
    clientSignOffCollateralDetails: GMRAJurisdictionalCollateralNettingDetails | null;
    clientSignOffCollateralSummary: CollateralisationMethodSummary;
}

export type JurisdictionalNettingInformation = ISDAJurisdictionalNettingInformation | GMRAJurisdictionalNettingInformation;

export interface JurisdictionalAnswers {
    question: string;
    answer: JSX.Element | null;
    showIcon?: boolean;
}

export interface BaseNettingResponseData {
    overallSummary: NettingEngineSummary;
    overallClientSummary: NettingEngineSummary;
}

export interface ISDANettingResponseData extends BaseNettingResponseData {
    jurisdictionalInformation: ISDAJurisdictionalNettingInformation[];
    sectionTenImpactsDecision: boolean;
}

export interface GMRANettingResponseData extends BaseNettingResponseData {
    jurisdictionalInformation: GMRAJurisdictionalNettingInformation[];
}

export type NettingResponseData = ISDANettingResponseData | GMRANettingResponseData;

export interface DownloadISDANettingResultsRequest extends ISDANettingRequestData, ISDANettingResponseData {
    assumptions: string[];
    nettingEngineAgreementType: AgreementType.ISDA;
}

export interface DownloadGMRANettingResultsRequest extends GMRANettingRequestData, GMRANettingResponseData {
    assumptions: string[];
    nettingEngineAgreementType: AgreementType.GMRA;
}

export type DownloadNettingResultsRequest = DownloadISDANettingResultsRequest | DownloadGMRANettingResultsRequest;

export interface EmailISDANettingResultsRequest extends DownloadISDANettingResultsRequest {
    userIds: number[];
    emailBody: RawDraftContentState | null;
}

export interface EmailGMRANettingResultsRequest extends DownloadGMRANettingResultsRequest {
    userIds: number[];
    emailBody: RawDraftContentState | null;
}

export type EmailNettingResultsRequest = DownloadISDANettingResultsRequest | DownloadGMRANettingResultsRequest;

export interface CountriesRequiringFiltering {
    countryGeoId: string;
    westernLatitude?: number;
    easternLatitude?: number;
    northernLongitude?: number;
    southernLongitude?: number;
}

export const countriesRequiringFiltering: CountriesRequiringFiltering[] = [
    { countryGeoId: 'USA', northernLongitude: 49, westernLatitude: -129 },
    { countryGeoId: 'RUS', westernLatitude: 23, easternLatitude: 180 },
    { countryGeoId: 'NOR', westernLatitude: -5, southernLongitude: 57, northernLongitude: 72 },
    { countryGeoId: 'NLD', southernLongitude: 51 },
    { countryGeoId: 'FRA', southernLongitude: 42 },
    { countryGeoId: 'ESP', southernLongitude: 35 },
    { countryGeoId: 'NZL', southernLongitude: -48, westernLatitude: -166 },
    { countryGeoId: 'PRT', westernLatitude: -9 },
    { countryGeoId: 'ECU', westernLatitude: -81 },
    { countryGeoId: 'CHL', westernLatitude: -76 },
    { countryGeoId: 'ASM', northernLongitude: -14, easternLatitude: -169.20 },
    { countryGeoId: 'BVT', northernLongitude: -54 },
    { countryGeoId: 'FJI', northernLongitude: -16, easternLatitude: 180, westernLatitude: 177 },
    { countryGeoId: 'GUF', northernLongitude: 6, easternLatitude: -51 },
    { countryGeoId: 'GLP', southernLongitude: 15.5, easternLatitude: -60 },
    { countryGeoId: 'MTQ', northernLongitude: 15, easternLatitude: -60 },
    { countryGeoId: 'KIR', northernLongitude: 2, southernLongitude: 1.6, easternLatitude: 172 },
    { countryGeoId: 'MDV', northernLongitude: 3.7, southernLongitude: 1.6 },
    { countryGeoId: 'PCN', easternLatitude: -127, westernLatitude: -129 },
    { countryGeoId: 'REU', westernLatitude: 55 },
    { countryGeoId: 'PLW', southernLongitude: 6.8 },
    { countryGeoId: 'TUV', westernLatitude: 178.3 },
    { countryGeoId: 'TKL', northernLongitude: -9.1, southernLongitude: -9.6 },
    { countryGeoId: 'MUS', northernLongitude: -19, easternLatitude: 58 },
    { countryGeoId: 'MYT', northernLongitude: -12, southernLongitude: -13 },
    { countryGeoId: 'SYC', northernLongitude: -4.2, southernLongitude: -4.8, westernLatitude: 55 },
    { countryGeoId: 'MHL', northernLongitude: 7.6 },
    { countryGeoId: 'COK', northernLongitude: -18 },
    { countryGeoId: 'IOT', northernLongitude: -6.1 },
    { countryGeoId: 'ATF', northernLongitude: -48.4 },
    { countryGeoId: 'FSM', northernLongitude: 7.1, southernLongitude: 6.7, westernLatitude: 158 },
    { countryGeoId: 'SHN', northernLongitude: -15.8 },
    { countryGeoId: 'SJM', southernLongitude: 76 },
    { countryGeoId: 'UMI', easternLatitude: 177 }
];

export interface SelectedJurisdictionDetails {
    question: string;
    value: string;
    details: RawDraftContentState | null;
    icon: string;
}

export interface DocumentNettingAutoUpdate {
    originalDocumentId: number;
    hasUpdatedDatasets: boolean;
    hasUpdatedEntities: boolean;
    parentInstanceIds: number[];
}

export interface NettingResultsEmailDetails {
    userIds: number[];
    emailBody: RawDraftContentState | null;
}

export interface OpinionNettingEngineState {
    nettingPage: NettingPage;
    selectedAgreementType: AgreementType | null;
    currentNettingEngineData: NettingEngineData | null;
    savedNettingEngineData: NettingEngineData | null;
    engineResults: NettingResponseData | null;
    theoreticalQuestion: string | null;
    calculatingResults: boolean;
    surveyQuestionIndex: number;
    showSummary: boolean;
    leiModalOpen: boolean;
    isChangingQuestion: boolean;
    nettingAssumptionsModalOpen: boolean;
    enquiredOpinions: string[];
    isCustomConclusion: boolean;
    nettingEngineDocumentFilters: TableFilters;
    nettingEngineDocumentSort?: ColumnSort;
    nettingEngineDocumentPageSize: number;
    isLoading: boolean;
    nettingEngineDocuments: ArkDocument[];
    totalNettingEngineDocuments: number;
    nettingEngineDocumentPage: number;
    isOpening: boolean;
    nettingEngineTableView: NettingEngineTableView;
    documentSummaryView: AnalysisView;
    selectedProductCovered: ProductTypeCovered | null;
    selectedJurisdictionDetails: SelectedJurisdictionDetails;
    answerJustificationModalOpen: boolean;
    isDownloadingNettingSummary: boolean;
    resultsEmailDetails: NettingResultsEmailDetails | null;
}
